import React, { useEffect, useState } from 'react';
import {
    Button,
    Checkbox,
    Col,
    Divider,
    Form,
    Input,
    Option,
    Row,
    Select,
    Tag,
    Text,
} from '@servahealth/serva-ui-kit';
import { CoreType, CoreDataPoint, CoreDataPointDataType } from '../Shared/CoreComponents';
import AdHocReportVariableWhereModel, { AdHocReportVariableWhereDataFormat, AdHocReportVariableWhereOperator, AdHocReportVariableWhereSelector } from '../../Interfaces/Requests/Reports/AdHocReportVariableWhereModel';

type WhereOption = {
    value: AdHocReportVariableWhereOperator;
    label: string;
};

export interface AHRBCoreDataPointVariableWhereProps {
    coreTypes: CoreType[];
    coreDataPoints: CoreDataPoint[];
    ahrbsDataPointID: number | undefined;
    selectedCoreDataPointColumnVariableWhere: AdHocReportVariableWhereModel | undefined;
    onOk: (
        dataPointVariableWhere: AdHocReportVariableWhereModel
    ) => void;
    onCancel: () => void;
}

export const AHRBCoreDataPointVariableWhere: React.FC<AHRBCoreDataPointVariableWhereProps> = ({ ...props }) => {

    const selectedDataPoint = props.coreDataPoints.find(cdp => cdp.id === props.ahrbsDataPointID);
    const selectedFieldName = selectedDataPoint?.name;
    const selectedFieldTypeID = selectedDataPoint?.idType;
    const selectedFieldDataType = selectedDataPoint?.dataType;
    const selectedTypeName = props.coreTypes.find(ct => ct.id === selectedFieldTypeID)?.name;

    const [formAHVariableWhere] = Form.useForm();

    const [selectedCoreDataPointVariableWhere, setSelectedCoreDataPointVariableWhere] = useState<AdHocReportVariableWhereOperator>(
        props.selectedCoreDataPointColumnVariableWhere?.where ?? AdHocReportVariableWhereOperator.None
    );

    const [title, setTitle] = useState<string>(
        props.selectedCoreDataPointColumnVariableWhere?.title ?? selectedFieldName ?? ''
    );

    const [inputName, setInputName] = useState<string>(
        props.selectedCoreDataPointColumnVariableWhere?.inputName ?? selectedFieldName ?? ''
    );

    const [inputNameWasManuallyEdited, setInputNameWasManuallyEdited] = useState<boolean>(() => {
        const existingTitle = props.selectedCoreDataPointColumnVariableWhere?.title?.trim() ?? '';
        const existingInputName = props.selectedCoreDataPointColumnVariableWhere?.inputName?.trim() ?? '';

        if (existingInputName.length === 0) {
            return false;
        }

        return existingInputName !== existingTitle;
    });
    
    useEffect(() => {
        if (!inputNameWasManuallyEdited) {
            setInputName(title);
        }
    }, [title, inputNameWasManuallyEdited]);

    const [selector, setSelector] = useState<AdHocReportVariableWhereSelector>(
        props.selectedCoreDataPointColumnVariableWhere?.selector ?? AdHocReportVariableWhereSelector.Text
    );

    const [dataFormat, setDataFormat] = useState<AdHocReportVariableWhereDataFormat>(
        props.selectedCoreDataPointColumnVariableWhere?.dataFormat ?? AdHocReportVariableWhereDataFormat.String
    );

    const [sourceQuery, setSourceQuery] = useState<string>(
        props.selectedCoreDataPointColumnVariableWhere?.sourceQuery ?? ''
    );

    const [multiSelect, setMultiSelect] = useState<boolean>(
        props.selectedCoreDataPointColumnVariableWhere?.multiSelect ?? false
    );

    const [required, setRequired] = useState<boolean>(
        props.selectedCoreDataPointColumnVariableWhere?.required ?? false
    );

    const [defaultValue, setDefaultValue] = useState<string>(
        props.selectedCoreDataPointColumnVariableWhere?.defaultValue ?? ''
    );

    const [changeOccurred, setChangeOccurred] = useState(false);
    
    const [sourceQueryTouched, setSourceQueryTouched] = useState(false);

    const handleWhereChange = (whereOperator: AdHocReportVariableWhereOperator) => {
        setChangeOccurred(true);
        setSelectedCoreDataPointVariableWhere(whereOperator);
    };

    const titleIsValid = title.trim().length > 0 && title.trim().length <= 100;

    const inputNameIsValid = inputName.trim().length === 0 || inputName.trim().length <= 100;

    const sourceQueryIsValid = selector !== AdHocReportVariableWhereSelector.Dropdown || sourceQuery.trim().length > 0;

    const showSourceQueryValidation = selector === AdHocReportVariableWhereSelector.Dropdown && sourceQueryTouched && !sourceQueryIsValid;
    const handleOk = () => {
        const existingVariableWhere = props.selectedCoreDataPointColumnVariableWhere;

        const dataPointVariableWhere: AdHocReportVariableWhereModel = {
            title: title.trim(),
            inputName: inputName.trim().length > 0 ? inputName.trim() : title.trim(),
            sourceQuery: selector === AdHocReportVariableWhereSelector.Dropdown ? sourceQuery.trim() : '',
            multiSelect: selector === AdHocReportVariableWhereSelector.Dropdown ? multiSelect : false,
            selector: selector,
            dataFormat: dataFormat,
            required: required,
            defaultValue: defaultValue.trim(),
            coreTypeID: existingVariableWhere?.coreTypeID ?? selectedFieldTypeID ?? 0,
            where: selectedCoreDataPointVariableWhere,
            valueLHS: usesRuntimeValue1 ? '{VAL1}' : '',
            valueRHS: usesRuntimeValue2 ? '{VAL2}' : '',
            variableWhereCoreDataPointModel: existingVariableWhere?.variableWhereCoreDataPointModel ?? selectedDataPoint!
        };

        props.onOk(dataPointVariableWhere);
    };

    const getAvailableWhereOptions = (dataType: CoreDataPointDataType | undefined): WhereOption[] => {
        switch (dataType) {
            case CoreDataPointDataType.Integer:
            case CoreDataPointDataType.Float:
            case CoreDataPointDataType.Autonumber:
                return [
                    { value: AdHocReportVariableWhereOperator.Equals, label: 'Equals' },
                    { value: AdHocReportVariableWhereOperator.NotEquals, label: 'Not Equals' },
                    { value: AdHocReportVariableWhereOperator.GreaterThan, label: 'Greater Than' },
                    { value: AdHocReportVariableWhereOperator.GreaterThanOrEqual, label: 'Greater Than Or Equal' },
                    { value: AdHocReportVariableWhereOperator.LessThan, label: 'Less Than' },
                    { value: AdHocReportVariableWhereOperator.LessThanOrEqual, label: 'Less Than Or Equal' },
                    { value: AdHocReportVariableWhereOperator.Between, label: 'Between' },
                    { value: AdHocReportVariableWhereOperator.IsNull, label: 'Is Null' },
                    { value: AdHocReportVariableWhereOperator.IsNotNull, label: 'Is Not Null' },
                ];

            case CoreDataPointDataType.Date:
            case CoreDataPointDataType.DateTime:
                return [
                    { value: AdHocReportVariableWhereOperator.Equals, label: 'Equals' },
                    { value: AdHocReportVariableWhereOperator.NotEquals, label: 'Not Equals' },
                    { value: AdHocReportVariableWhereOperator.GreaterThan, label: 'Greater Than' },
                    { value: AdHocReportVariableWhereOperator.GreaterThanOrEqual, label: 'Greater Than Or Equal' },
                    { value: AdHocReportVariableWhereOperator.LessThan, label: 'Less Than' },
                    { value: AdHocReportVariableWhereOperator.LessThanOrEqual, label: 'Less Than Or Equal' },
                    { value: AdHocReportVariableWhereOperator.Between, label: 'Between' },
                    { value: AdHocReportVariableWhereOperator.IsNull, label: 'Is Null' },
                    { value: AdHocReportVariableWhereOperator.IsNotNull, label: 'Is Not Null' },
                ];

            case CoreDataPointDataType.String:
            case CoreDataPointDataType.Phonenumber:
            case CoreDataPointDataType.Lookup:
                return [
                    { value: AdHocReportVariableWhereOperator.Equals, label: 'Equals' },
                    { value: AdHocReportVariableWhereOperator.NotEquals, label: 'Not Equals' },
                    { value: AdHocReportVariableWhereOperator.Contains, label: 'Contains' },
                    { value: AdHocReportVariableWhereOperator.StartsWith, label: 'Starts With' },
                    { value: AdHocReportVariableWhereOperator.EndsWith, label: 'Ends With' },
                    { value: AdHocReportVariableWhereOperator.IsNull, label: 'Is Null' },
                    { value: AdHocReportVariableWhereOperator.IsNotNull, label: 'Is Not Null' },
                ];

            default:
                return [];
        }
    };

    const availableWhereOptions = getAvailableWhereOptions(selectedFieldDataType);    
    
    const selectedWhereIsValid =
        selectedCoreDataPointVariableWhere === AdHocReportVariableWhereOperator.None ||
        availableWhereOptions.some(option => option.value === selectedCoreDataPointVariableWhere);

    const hadExistingWhere =
        props.selectedCoreDataPointColumnVariableWhere?.where !== undefined &&
        props.selectedCoreDataPointColumnVariableWhere.where !== AdHocReportVariableWhereOperator.None;

    const isRemovingWhere =
        hadExistingWhere &&
        changeOccurred &&
        selectedCoreDataPointVariableWhere === AdHocReportVariableWhereOperator.None;

    const usesRuntimeValue1 =
        selectedCoreDataPointVariableWhere !== AdHocReportVariableWhereOperator.None &&
        selectedCoreDataPointVariableWhere !== AdHocReportVariableWhereOperator.IsNull &&
        selectedCoreDataPointVariableWhere !== AdHocReportVariableWhereOperator.IsNotNull;

    const usesRuntimeValue2 =
        selectedCoreDataPointVariableWhere === AdHocReportVariableWhereOperator.Between;

    const hasValidWhereConfiguration =
        (
            selectedCoreDataPointVariableWhere !== AdHocReportVariableWhereOperator.None &&
            selectedWhereIsValid &&
            sourceQueryIsValid &&
            titleIsValid &&
            inputNameIsValid
        ) ||
        isRemovingWhere;

    const contentStyle: React.CSSProperties = {
        lineHeight: '95%',
        textAlign: 'center',
        color: 'black',
        backgroundColor: 'azure',
        borderRadius: '10',
        border: `1px solid black`,
        marginTop: 4,
        height: '95%'
    };

    return (
        <Form
            form={formAHVariableWhere}
            initialValues={{
                ahvwTitle: props.selectedCoreDataPointColumnVariableWhere?.title ?? selectedFieldName ?? '',
                ahvwInputName: props.selectedCoreDataPointColumnVariableWhere?.inputName ?? selectedFieldName ?? '',
                ahvwSelector: props.selectedCoreDataPointColumnVariableWhere?.selector ?? AdHocReportVariableWhereSelector.Text,
                ahvwDataFormat: props.selectedCoreDataPointColumnVariableWhere?.dataFormat ?? AdHocReportVariableWhereDataFormat.String,
                ahvwSourceQuery: props.selectedCoreDataPointColumnVariableWhere?.sourceQuery ?? '',
                ahvwMultiSelect: props.selectedCoreDataPointColumnVariableWhere?.multiSelect ?? false,
                ahvwRequired: props.selectedCoreDataPointColumnVariableWhere?.required ?? false,
                ahvwDefaultValue: props.selectedCoreDataPointColumnVariableWhere?.defaultValue ?? '',
            }}
        >
            <>
                <Row gutter={16} className='retainbi-compact-row'>
                    <Col span={4} />
                    <Col span={16}>
                        <div style={contentStyle}>
                            <h3 className='text-2xl pt-2 px-2'><u>Variable Where</u></h3>
                            <h5 className='text-2xl pt-2 px-2'><i>User Supplied Input Filter</i></h5>
                        </div>
                    </Col>
                    <Col span={4} />
                </Row>
                <Row gutter={16} className='retainbi-compact-row'>
                    <Col span={1} />
                    <Col span={22}>
                        <div>
                            <Divider style={{ borderTop: '1px solid black', margin: '1px' }}></Divider>
                        </div>
                    </Col>
                    <Col span={1} />
                </Row>
                <Row gutter={16} style={{ backgroundColor: 'palegreen', borderRadius: '8px', padding: '16px', paddingBottom: '4px', paddingTop: '4px' }}>
                    <Col span={1} />
                    <Col span={11}>
                        <Text italic strong>Field:</Text>{" "}
                        <Text>{selectedFieldName}</Text>
                    </Col>
                    <Col span={11}>
                        <Text italic strong>Type:</Text>{" "}
                        <Text>{selectedTypeName}</Text>
                    </Col>
                    <Col span={1} />
                </Row>
                <Row gutter={16} className='retainbi-compact-row'>
                    <Col span={24}>
                        <div>
                            <Divider style={{ margin: '1px' }}></Divider>
                        </div>
                    </Col>
                </Row>
                <Row gutter={16} className='retainbi-compact-row'>
                    <Col span={12}>
                        <Form.Item
                            key='ahvwtitle'
                            name='ahvwTitle'
                            help={titleIsValid ? undefined : 'Title is required.'}
                            label='Title'
                            labelCol={{ span: 8 }}
                            validateStatus={titleIsValid ? undefined : 'error'}
                            style={{ marginBottom: '4px' }}
                        >
                            <Input
                                placeholder="Enter Title"
                                value={title}
                                onChange={(e) => {
                                    const newTitle = e.target.value;

                                    setChangeOccurred(true);
                                    setTitle(newTitle);

                                    if (!inputNameWasManuallyEdited) {
                                        setInputName(newTitle);
                                    }
                                }}
                            />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            key='ahvwinputname'
                            name='ahvwInputName'
                            help={inputNameIsValid ? undefined : 'Input Name is invalid.'}
                            label='Input Name'
                            labelCol={{ span: 8, }}
                            validateStatus={inputNameIsValid ? undefined : 'error'}
                            style={{marginBottom: '4px'}}
                        >
                            <Input
                                placeholder="Enter Input Name"
                                value={inputName}
                                onChange={(e) => {
                                    const newInputName = e.target.value;

                                    setChangeOccurred(true);
                                    setInputName(newInputName);
                                    setInputNameWasManuallyEdited(newInputName.trim() !== title.trim());
                                }}
                            />
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={16} className='retainbi-compact-row'>
                    <Col span={24}>
                        <div>
                            <Divider style={{ margin: '1px' }}></Divider>
                        </div>
                    </Col>
                </Row>
                <Row gutter={16} className='retainbi-compact-row'>
                    <Col span={6}>
                        <div style={{textAlign:'left'}}>
                            <Form.Item
                                name='ahvwSelector'
                                style={{ marginBottom: '0px' }}
                            >
                                <Select
                                    label='Selector Type:'
                                    value={selector}
                                    onChange={(value: AdHocReportVariableWhereSelector) => {
                                        setChangeOccurred(true);
                                        setSelector(value);
                                        setSelectedCoreDataPointVariableWhere(AdHocReportVariableWhereOperator.None);

                                        if (value !== AdHocReportVariableWhereSelector.Dropdown) {
                                            setMultiSelect(false);
                                            setSourceQuery('');
                                            setSourceQueryTouched(false);
                                            setDataFormat(AdHocReportVariableWhereDataFormat.String);
                                        }

                                        if (value === AdHocReportVariableWhereSelector.DatePicker) {
                                            setDataFormat(AdHocReportVariableWhereDataFormat.DateTime);
                                        }
                                        else if (value === AdHocReportVariableWhereSelector.Numeric) {
                                            setDataFormat(AdHocReportVariableWhereDataFormat.Number);
                                        }
                                        else if (value === AdHocReportVariableWhereSelector.Text) {
                                            setDataFormat(AdHocReportVariableWhereDataFormat.String);
                                        }
                                        else if (value === AdHocReportVariableWhereSelector.Between) {
                                            if (
                                                selectedFieldDataType === CoreDataPointDataType.Date ||
                                                selectedFieldDataType === CoreDataPointDataType.DateTime
                                            ) {
                                                setDataFormat(AdHocReportVariableWhereDataFormat.DateTime);
                                            }
                                            else if (
                                                selectedFieldDataType === CoreDataPointDataType.Integer ||
                                                selectedFieldDataType === CoreDataPointDataType.Float ||
                                                selectedFieldDataType === CoreDataPointDataType.Autonumber
                                            ) {
                                                setDataFormat(AdHocReportVariableWhereDataFormat.Number);
                                            }
                                            else {
                                                setDataFormat(AdHocReportVariableWhereDataFormat.String);
                                            }
                                        }
                                    }}
                                >
                                    {
                                        (
                                            selectedFieldDataType === CoreDataPointDataType.Date ||
                                            selectedFieldDataType === CoreDataPointDataType.DateTime ||
                                            selectedFieldDataType === CoreDataPointDataType.Integer ||
                                            selectedFieldDataType === CoreDataPointDataType.Float ||
                                            selectedFieldDataType === CoreDataPointDataType.Autonumber
                                        ) && (
                                            <Option value={AdHocReportVariableWhereSelector.Between} label='Between'>
                                                Between
                                            </Option>
                                        )
                                    }
                                    <Option value={AdHocReportVariableWhereSelector.DatePicker} label='Date Picker'>
                                        Date Picker
                                    </Option>
                                    <Option value={AdHocReportVariableWhereSelector.Dropdown} label='Dropdown'>
                                        Dropdown
                                    </Option>
                                    <Option value={AdHocReportVariableWhereSelector.Numeric} label='Numeric'>
                                        Numeric
                                    </Option>
                                    <Option value={AdHocReportVariableWhereSelector.Text} label='Text'>
                                        Text
                                    </Option>
                                </Select>
                            </Form.Item>
                        </div>
                    </Col>
                    <Col span={6}>
                        <div style={{textAlign:'left'}}>
                            <Form.Item
                                name='ahvwDataFormat'
                                style={{ marginBottom: '0px' }}
                            >
                                <Select
                                    disabled={selector !== AdHocReportVariableWhereSelector.Dropdown}
                                    label='Data Format:'
                                    value={dataFormat}
                                    onChange={(value: AdHocReportVariableWhereDataFormat) => {
                                        setChangeOccurred(true);
                                        setDataFormat(value);

                                        if (value !== AdHocReportVariableWhereDataFormat.String &&
                                            selector === AdHocReportVariableWhereSelector.Dropdown) {
                                            setMultiSelect(false);
                                        }
                                    }}
                                >
                                    <Option value={AdHocReportVariableWhereDataFormat.DateTime} label='DateTime'>
                                        DateTime
                                    </Option>
                                    <Option value={AdHocReportVariableWhereDataFormat.Number} label='Number'>
                                        Number
                                    </Option>
                                    <Option value={AdHocReportVariableWhereDataFormat.String} label='String'>
                                        String
                                    </Option>
                                </Select>
                            </Form.Item>
                        </div>
                    </Col>
                    <Col span={2} />
                    <Col span={5}>
                        <Form.Item
                            key='ahvwmultiselect'
                            name='ahvwMultiSelect'
                            valuePropName='checked'
                            style={{marginTop: '26px'}}
                        >
                            <Checkbox
                                checked={multiSelect}
                                disabled={
                                    selector !== AdHocReportVariableWhereSelector.Dropdown ||
                                    dataFormat !== AdHocReportVariableWhereDataFormat.String
                                }
                                style={{float: 'left'}}
                                onChange={(e) => {
                                    setChangeOccurred(true);
                                    setMultiSelect(e.target.checked);
                                }}
                            >
                                Multi Select
                            </Checkbox>
                        </Form.Item>
                    </Col>
                    <Col span={5}>
                        <Form.Item
                            key='ahvwrequired'
                            name='ahvwRequired'
                            valuePropName='checked'
                            style={{marginTop: '26px'}}
                        >
                            <Checkbox
                                checked={required}
                                style={{float: 'left'}}
                                onChange={(e) => {
                                    setChangeOccurred(true);
                                    setRequired(e.target.checked);
                                }}
                            >
                                Required
                            </Checkbox>
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={16} className='retainbi-compact-row'>
                    <Col span={24}>
                        <div>
                            <Divider style={{ margin: '1px' }}></Divider>
                        </div>
                    </Col>
                </Row>
                <Row gutter={16} className='retainbi-compact-row'>
                    <Col span={24}>
                        <Form.Item
                            key='ahvwsourcequery'
                            name='ahvwSourceQuery'
                            help={showSourceQueryValidation ? 'Source Query is required for Dropdown selector.' : undefined}
                            label='Source Query'
                            labelCol={{ span: 4 }}
                            validateStatus={showSourceQueryValidation ? 'error' : undefined}
                            style={{ marginBottom: '2px' }}
                        >
                            <Input
                                disabled={selector !== AdHocReportVariableWhereSelector.Dropdown}
                                placeholder="Enter Source Query"
                                value={sourceQuery}
                                onChange={(e) => {
                                    const newSourceQuery = e.target.value;

                                    setChangeOccurred(true);
                                    setSourceQueryTouched(true);
                                    setSourceQuery(newSourceQuery);
                                }}
                            />
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={16} className='retainbi-compact-row'>
                    <Col span={24}>
                        <div>
                            <Divider style={{ margin: '1px' }}></Divider>
                        </div>
                    </Col>
                </Row>
                <Row gutter={16} className='retainbi-compact-row'>
                    <Col span={24}>
                        <Form.Item
                            key='ahvwdefaultvalue'
                            name='ahvwDefaultValue'
                            label='Default Value'
                            labelCol={{ span: 4 }}
                            style={{ marginBottom: '2px' }}
                        >
                            <Input
                                placeholder="Enter Default Value"
                                value={defaultValue}
                                onChange={(e) => {
                                    const newDefaultValue = e.target.value;

                                    setChangeOccurred(true);
                                    setDefaultValue(newDefaultValue);
                                }}
                            />
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={16} className='retainbi-compact-row'>
                    <Col span={24}>
                        <div>
                            <Divider style={{ margin: '1px' }}></Divider>
                        </div>
                    </Col>
                </Row>
                <Row gutter={16} className='retainbi-compact-row'>
                    <Col span={10}>
                        <Form.Item
                            label='Filter (Where)'
                            labelCol={{ span: 10 }}
                            style={{ marginBottom: '0px' }}
                        >
                            <Select
                                showSearch
                                style={{ width: '100%' }}
                                value={selectedCoreDataPointVariableWhere}
                                onChange={handleWhereChange}
                            >
                                <Option value={AdHocReportVariableWhereOperator.None} label={'(Not Set)'}>
                                    {'(Not Set)'}
                                </Option>
                                {
                                    availableWhereOptions.map(whereOption => (
                                        <Option
                                            key={whereOption.value}
                                            value={whereOption.value}
                                            label={whereOption.label}
                                        >
                                            {whereOption.label}
                                        </Option>
                                    ))
                                }
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col span={7}>
                        <Form.Item
                            label='Runtime Value 1'
                            labelCol={{ span: 12 }}
                            style={{ marginBottom: '4px' }}
                        >
                            <div style={{ minHeight: '32px', display: 'flex', alignItems: 'center' }}>
                                {
                                    usesRuntimeValue1 ?
                                    <Tag
                                        style={{
                                            backgroundColor: '#f6ffed',
                                            borderColor: 'lightgreen',
                                            color: 'green',
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            height: '24px'
                                        }}
                                    >
                                        {'{VAL1}'}
                                    </Tag> :
                                    <Tag
                                        style={{
                                            backgroundColor: '#f6ffed',
                                            borderColor: 'lightgreen',
                                            color: 'green',
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            height: '24px'
                                        }}
                                    >
                                        {'N / A'}
                                    </Tag>
                                }
                            </div>
                        </Form.Item>
                    </Col>
                    <Col span={7}>
                        <Form.Item
                            label='Runtime Value 2'
                            labelCol={{ span: 12 }}
                            style={{ marginBottom: '4px' }}
                        >
                            <div style={{ minHeight: '32px', display: 'flex', alignItems: 'center' }}>
                                {
                                    usesRuntimeValue2 ?
                                    <Tag
                                        style={{
                                            backgroundColor: '#f6ffed',
                                            borderColor: 'lightgreen',
                                            color: 'green',
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            height: '24px'
                                        }}
                                    >
                                        {'{VAL2}'}
                                    </Tag> :
                                    <Tag
                                        style={{
                                            backgroundColor: '#f6ffed',
                                            borderColor: 'lightgreen',
                                            color: 'green',
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            height: '24px'
                                        }}
                                    >
                                        {'N / A'}
                                    </Tag>
                                }
                            </div>
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={16} className='retainbi-compact-row'>
                    <Col span={24}>
                        <div>
                            <Divider style={{ margin: '1px' }}></Divider>
                        </div>
                    </Col>
                </Row>
                <Row gutter={16} className='retainbi-compact-row'>
                    <Col span={15} />
                    <Col span={4}>
                        <div style={{ textAlign: 'right' }}>
                            <Button
                                disabled={!changeOccurred || !hasValidWhereConfiguration}
                                htmlType="button"
                                style={{width: '95%'}}
                                type='primary'
                                onClick={handleOk}
                            >
                                OK
                            </Button>
                        </div>
                    </Col>
                    <Col span={4}>
                        <div style={{ textAlign: 'right' }}>
                                <Button
                                    style={{width: '95%'}}
                                    type="default"
                                    onClick={() => props.onCancel()}
                                >
                                    Cancel
                                </Button>
                        </div>
                    </Col>
                    <Col span={1} />
                </Row>
            </>
        </Form>
    );
};

export default AHRBCoreDataPointVariableWhere;